import React from 'react';
import { Globe, Mail, Phone, MessageSquare, MapPin, Search, Heart, ShoppingCart, User, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { resolveMediaUrl, handleImageError } from '../../../utils/mediaResolver';

export default function SettingsPreview({ form = {} }) {
  const websiteName = form.website_name || 'ZOBOX';

  return (
    <div className="bg-slate-50 min-h-full flex flex-col font-sans text-gray-900">
      {/* Top Header Simulation Bar */}
      <div className="bg-white border-b px-4 py-2.5 flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-500">Website Global Shell &amp; Identity Preview</span>
        <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-bold uppercase text-[10px]">
          Global Settings
        </span>
      </div>

      <div className="p-6 md:p-8 space-y-8 flex-1 max-w-4xl mx-auto w-full">
        {/* 1. Browser Tab Simulation */}
        <div className="space-y-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
            1. Browser Tab &amp; Identity Preview
          </span>
          <div className="bg-slate-200 p-2 rounded-xl border border-slate-300 flex items-center gap-2 max-w-sm">
            <div className="w-5 h-5 rounded bg-white p-0.5 flex items-center justify-center overflow-hidden border">
              {form.favicon ? (
                <img
                  src={resolveMediaUrl(form.favicon)}
                  alt=""
                  className="w-full h-full object-contain"
                  onError={handleImageError}
                />
              ) : (
                <Globe className="w-3.5 h-3.5 text-blue-600" />
              )}
            </div>
            <span className="text-xs font-semibold text-slate-800 truncate">
              {form.default_seo_title || `${websiteName} | Refurbished Devices & Stores`}
            </span>
          </div>
        </div>

        {/* 2. Global Header Shell */}
        <div className="space-y-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
            2. Global Storefront Header
          </span>
          <div className="bg-slate-900 text-white rounded-2xl p-4 shadow-lg flex flex-wrap items-center justify-between gap-4">
            {/* Logo */}
            <div className="flex items-center gap-3">
              {form.logo ? (
                <img
                  src={resolveMediaUrl(form.logo)}
                  alt={form.logo_alt || websiteName}
                  className="h-8 max-w-[140px] object-contain"
                  onError={handleImageError}
                />
              ) : (
                <span className="text-xl font-black tracking-wider text-white">{websiteName}</span>
              )}
            </div>

            {/* Search Bar */}
            <div className="flex-1 max-w-xs hidden sm:flex items-center gap-2 bg-slate-800 border border-slate-700 px-3 py-1.5 rounded-lg text-xs text-slate-400">
              <Search className="w-3.5 h-3.5" />
              <span>{form.search_placeholder || 'Search devices...'}</span>
            </div>

            {/* Header Right Actions */}
            <div className="flex items-center gap-4 text-xs font-semibold">
              {form.support_label && (
                <span className="text-blue-200 hover:text-white flex items-center gap-1">
                  {form.support_label}
                </span>
              )}
              <div className="flex items-center gap-3 text-slate-300">
                <span className="flex items-center gap-1 hover:text-white" title={form.wishlist_label || 'Wishlist'}>
                  <Heart className="w-4 h-4" />
                  <span className="hidden md:inline text-[11px]">{form.wishlist_label || 'Wishlist'}</span>
                </span>
                <span className="flex items-center gap-1 hover:text-white" title={form.cart_label || 'Cart'}>
                  <ShoppingCart className="w-4 h-4" />
                  <span className="hidden md:inline text-[11px]">{form.cart_label || 'Cart'}</span>
                </span>
                <span className="flex items-center gap-1 hover:text-white" title={form.account_label || 'Account'}>
                  <User className="w-4 h-4" />
                  <span className="hidden md:inline text-[11px]">{form.account_label || 'Account'}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Global Contact & Support Strip */}
        <div className="space-y-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
            3. Contact Details &amp; Social Links
          </span>
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="space-y-3">
              <strong className="text-slate-800 block text-sm">Customer Touchpoints</strong>
              {form.email && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>{form.email}</span>
                </div>
              )}
              {form.phone && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span>{form.phone}</span>
                </div>
              )}
              {form.whatsapp && (
                <div className="flex items-center gap-2 text-slate-600">
                  <MessageSquare className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="truncate">{form.whatsapp}</span>
                </div>
              )}
              {form.address && (
                <div className="flex items-start gap-2 text-slate-600">
                  <MapPin className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="whitespace-pre-line">{form.address}</span>
                </div>
              )}
            </div>

            <div className="space-y-3 border-t md:border-t-0 md:border-l md:pl-4">
              <strong className="text-slate-800 block text-sm">
                {form.social_label || 'Follow ZOBOX'}
              </strong>
              <div className="flex gap-3 text-slate-700">
                {form.facebook && <Facebook className="w-5 h-5 text-blue-600" />}
                {form.instagram && <Instagram className="w-5 h-5 text-pink-600" />}
                {form.linkedin && <Linkedin className="w-5 h-5 text-blue-700" />}
                {form.youtube && <Youtube className="w-5 h-5 text-red-600" />}
              </div>
              {form.google_maps_url && (
                <div className="pt-2">
                  <span className="text-xs text-blue-700 underline font-medium">
                    Google Maps Verification Link configured
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 4. Default SEO Card */}
        <div className="space-y-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
            4. Default Search Engine Meta Preview
          </span>
          <div className="bg-white p-4 rounded-xl border border-slate-200 text-xs space-y-1">
            <span className="text-blue-700 font-bold text-sm">
              {form.default_seo_title || `${websiteName} | Official Website`}
            </span>
            <div className="text-emerald-700 text-[11px]">https://zobox.in</div>
            <p className="text-slate-600">
              {form.default_seo_description || 'Buy quality refurbished devices with warranty and quick delivery.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
